import { useEffect, useMemo, useRef, useState } from 'react'
import { Avatar, Button, Dropdown, Layout, Menu, Space, Spin, theme, Typography, message } from 'antd'
import {
  AimOutlined,
  AppstoreOutlined,
  BellOutlined,
  BookOutlined,
  CopyOutlined,
  DiffOutlined,
  GlobalOutlined,
  HomeOutlined,
  LogoutOutlined,
  MessageOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  ShareAltOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import type { MenuProps } from 'antd'
import { adminApi, getAdminDisplayName, getAdminToken, openAdminInNewTab, setAdminToken } from '../api'
import { hasAdminMenuAccess, type AdminMenuRow } from '../lib/adminMenus'
import { ensureAuthenticated } from '../lib/requireAuth'
import ChangePasswordModal from '../components/ChangePasswordModal'
import { TransferManager } from '../components/TransferManager'
import { useMustChangePassword } from '../lib/useMustChangePassword'

const { Header, Sider, Content } = Layout
const { Text } = Typography

const SIDER_WIDTH = 232
const ADMIN_MENU_KEY = 'admin-entry'
const APP_CENTER_KEY = 'app-center'
const APP_CHILD_ROUTES = ['/', '/chat', '/search', '/graph', '/dedup', '/compare', '/locate', '/desensitize']

export default function PublicLayout() {
  const { token } = theme.useToken()
  const loc = useLocation()
  const navigate = useNavigate()
  const [adminSession, setAdminSession] = useState(() => ({
    loggedIn: !!getAdminToken(),
    name: getAdminDisplayName(),
  }))
  const [authChecking, setAuthChecking] = useState(true)
  const [adminMenus, setAdminMenus] = useState<AdminMenuRow[]>([])
  const {
    mustChangePassword,
    applyMustChangePassword,
    refreshMustChangePassword,
    deferPasswordChange,
    completePasswordChange,
  } = useMustChangePassword()

  const [openKeys, setOpenKeys] = useState<string[]>([APP_CENTER_KEY])
  const showAdminEntry = hasAdminMenuAccess(adminMenus)

  const syncAdminSession = () => {
    setAdminSession({ loggedIn: !!getAdminToken(), name: getAdminDisplayName() })
  }

  useEffect(() => {
    let cancelled = false
    void ensureAuthenticated().then((ok) => {
      if (cancelled) return
      if (!ok) {
        const redirect = `${loc.pathname}${loc.search}`
        void navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true })
        setAuthChecking(false)
        return
      }
      syncAdminSession()
      void loadAdminMenus().finally(() => {
        if (!cancelled) setAuthChecking(false)
      })
    })
    return () => {
      cancelled = true
    }
  }, [loc.pathname, loc.search, navigate])

  const loadAdminMenus = () => {
    if (!getAdminToken()) {
      setAdminMenus([])
      return Promise.resolve()
    }
    return adminApi
      .get<{ menus: AdminMenuRow[]; user: { must_change_password?: boolean } }>('/admin/auth/me')
      .then((r) => {
        setAdminMenus(r.data.menus)
        applyMustChangePassword(!!r.data.user.must_change_password)
      })
      .catch(() => {
        setAdminMenus([])
        applyMustChangePassword(false)
      })
  }

  useEffect(() => {
    const sync = () => {
      syncAdminSession()
      void loadAdminMenus()
      void refreshMustChangePassword()
    }
    window.addEventListener('admin-session-sync', sync)
    window.addEventListener('focus', sync)
    return () => {
      window.removeEventListener('admin-session-sync', sync)
      window.removeEventListener('focus', sync)
    }
  }, [])

  const logout = () => {
    setAdminToken(null)
    setAdminSession({ loggedIn: false, name: null })
    message.success('已退出登录')
    void navigate('/login', { replace: true })
  }

  const adminMenu: MenuProps = {
    onClick: ({ key }) => {
      if (key === 'admin') openAdminInNewTab()
      if (key === 'logout') logout()
    },
    items: [
      ...(showAdminEntry
        ? [{ key: 'admin', icon: <SettingOutlined />, label: '进入后台管理' }]
        : []),
      ...(showAdminEntry ? [{ type: 'divider' as const }] : []),
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
    ],
  }

  const selectedKeys = useMemo(() => {
    if (loc.pathname === '/locate' || loc.pathname.startsWith('/locate/')) return ['/locate']
    if (APP_CHILD_ROUTES.includes(loc.pathname)) return [loc.pathname]
    return [loc.pathname]
  }, [loc.pathname])

  useEffect(() => {
    if (APP_CHILD_ROUTES.includes(loc.pathname) || loc.pathname.startsWith('/locate/')) {
      setOpenKeys((prev) => (prev.includes(APP_CENTER_KEY) ? prev : [...prev, APP_CENTER_KEY]))
    }
  }, [loc.pathname])

  const isLocateWorkspace = loc.pathname.startsWith('/locate/') && loc.pathname !== '/locate'
  const isWorkspace = loc.pathname === '/chat' || loc.pathname === '/graph' || isLocateWorkspace
  const isSearchPage = loc.pathname === '/search'
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
  }, [loc.pathname])

  const items = useMemo(() => {
    const base: NonNullable<MenuProps['items']> = [
      {
        key: APP_CENTER_KEY,
        icon: <HomeOutlined />,
        label: '应用中心',
        children: [
          { key: '/', label: '应用概览' },
          { key: '/chat', icon: <MessageOutlined />, label: 'AI 对话' },
          { key: '/search', icon: <SearchOutlined />, label: '智能搜索' },
          { key: '/graph', icon: <ShareAltOutlined />, label: '知识图谱' },
          { key: '/dedup', icon: <CopyOutlined />, label: '文件查重' },
          { key: '/compare', icon: <DiffOutlined />, label: '文档比对' },
          { key: '/desensitize', icon: <SafetyCertificateOutlined />, label: '文档脱敏' },
          { key: '/locate', icon: <AimOutlined />, label: '文档定位' },
        ],
      },
      { key: '/enterprise', icon: <TeamOutlined />, label: '企业知识库' },
      { key: '/knowledge', icon: <BookOutlined />, label: '个人知识库' },
    ]
    if (showAdminEntry) {
      base.push(
        { type: 'divider' },
        { key: ADMIN_MENU_KEY, icon: <SettingOutlined />, label: '后台管理' },
      )
    }
    return base
  }, [showAdminEntry])

  const onMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === ADMIN_MENU_KEY) {
      openAdminInNewTab()
      return
    }
    if (typeof key === 'string' && key.startsWith('/')) void navigate(key)
  }

  if (authChecking) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" description="正在校验登录状态…" />
      </div>
    )
  }

  return (
    <>
      <ChangePasswordModal
        open={mustChangePassword}
        onDefer={deferPasswordChange}
        onSuccess={() => {
          completePasswordChange()
          void refreshMustChangePassword()
        }}
      />
      <Layout className="public-shell">
      <Sider width={SIDER_WIDTH} className="public-sider" theme="dark">
        <div className="public-sider-brand">
          <span className="public-sider-logo" aria-hidden>
            <RobotOutlined />
          </span>
          <Text strong className="public-sider-title">
            企业 AI 知识库
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          items={items}
          onClick={onMenuClick}
          className="public-sider-menu"
        />
      </Sider>
      <Layout className="public-main">
        <Header
          className="public-topbar"
          style={{ background: token.colorBgContainer, borderBottom: `1px solid ${token.colorBorderSecondary}` }}
        >
          <div />
          <Space size={20} className="public-topbar-actions">
            {adminSession.loggedIn && <TransferManager />}
            <AppstoreOutlined className="public-topbar-icon" />
            <GlobalOutlined className="public-topbar-icon" />
            <BellOutlined className="public-topbar-icon" />
            {adminSession.loggedIn ? (
              <Dropdown menu={adminMenu} trigger={['click']} placement="bottomRight">
                <Space size={8} className="public-topbar-admin-trigger">
                  <Avatar size={28} style={{ background: token.colorPrimary, cursor: 'pointer' }}>
                    {(adminSession.name || '管').slice(0, 1)}
                  </Avatar>
                  <Text style={{ cursor: 'pointer' }}>{adminSession.name || '管理员'}</Text>
                </Space>
              </Dropdown>
            ) : (
              <Button type="link" onClick={() => void navigate('/login')}>
                登录
              </Button>
            )}
          </Space>
        </Header>
        <Content
          ref={contentRef}
          className={`public-content${isWorkspace ? ' public-content--fill' : ''}${isSearchPage ? ' public-content--search' : ''}`}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
    </>
  )
}
